import { Component, OnInit, OnDestroy, Inject, DOCUMENT } from '@angular/core';
import { Router, NavigationStart, NavigationEnd, NavigationCancel, NavigationError } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

// Styles from legacy dashboard that still need including
import 'dashboard/css/style.less';
import 'go-styles/src/goreact-style.less';
import { UserService, userServiceToken } from 'go-modules/models/user/user.service';
import { NgxAuthService } from '@go-modules/services/auth/auth.service';
import { AppDataService, appDataServiceToken } from 'dashboard/services/app-data.service';
import { LogoutSubscribeOptions } from '@go-modules/services/auth/interfaces/logout-subscribe-options.interface';
import { PWAService } from '@go-modules/services/pwa/pwa.service';
import { NgxUserpilotService } from '@go-modules/services/userpilot/userpilot.service';
import { buildUserpilotIdentifyProperties } from '@go-modules/services/userpilot/userpilot-identify-properties';
import { SelectedService, selectedServiceToken } from 'go-modules/services/selected/selected.service';

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
	standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
	public loading: boolean = false;
	private destroy$ = new Subject<void>();
	private userpilotIdentified = false;

	constructor (
		private router: Router,
		private ngxAuthService: NgxAuthService,
		private ngxPWAService: PWAService,
		private userpilotService: NgxUserpilotService,
		@Inject(userServiceToken) private userService: UserService,
		@Inject(appDataServiceToken) private AppData: AppDataService,
		@Inject(selectedServiceToken) private selectedService: SelectedService,
		@Inject(DOCUMENT) private document: Document
	) {
		// Setup PWA as early as possible to prevent native install prompts
		this.ngxPWAService.init();
	}

	public ngOnInit (): void {
		// Listen to router events for loading state
		// Only show loading when navigating to/from auth routes, initial dashboard load, or first page load
		this.router.events
			.pipe(takeUntil(this.destroy$))
			.subscribe((event) => {
				if (event instanceof NavigationStart) {
					// Show loading at app-root level for:
					// 1. Auth routes
					// 2. Dashboard root navigation
					// 3. First page load (no navigation ID means initial navigation)
					const isAuthRoute = event.url.includes('/auth/');
					const isDashboardRoot = event.url === '/dashboard' || event.url.startsWith('/dashboard?');
					const isFirstPageLoad = event.id === 1;
					this.loading = isAuthRoute || isDashboardRoot || isFirstPageLoad;
				} else if (
					event instanceof NavigationEnd ||
					event instanceof NavigationCancel ||
					event instanceof NavigationError
				) {
					if (event instanceof NavigationEnd) {
						void this.handleUserpilotNavigation(event.urlAfterRedirects);
					}
					this.loading = false;
				}
			});

		// Setup user service subscription
		this.userService.userSubject
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => {
				const body = this.document.defaultView?.document.body;
				if (body) {
					if (this.userService.currentUser.is_root_user) {
						body.classList.add('super');
					} else {
						body.classList.remove('super');
					}
				}
			});

		// Reset all services that had been instantiated with user information
		this.ngxAuthService.authLogoutSubject
			.pipe(takeUntil(this.destroy$))
			.subscribe((options?: LogoutSubscribeOptions) => {
				this.userpilotIdentified = false;
				this.AppData.resetUserRelatedServices(options);
			});
	}

	public ngOnDestroy (): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private async handleUserpilotNavigation (url: string): Promise<void> {
		await this.identifyUserpilot(url);
		await this.userpilotService.reload();
	}

	private async identifyUserpilot (url: string): Promise<void> {
		if (this.userpilotIdentified || this.shouldSkipUserpilotIdentify(url)) {
			return;
		}

		const user = this.AppData.user ?? {};
		if (user.user_id == null) {
			return;
		}

		await this.userpilotService.identify(user.user_id, buildUserpilotIdentifyProperties({
			surface: 'dashboard',
			user,
			org: this.selectedService.getOrg(),
			hasInstructorRole: this.AppData.hasInstructorRole(true),
			hasReviewerRole: this.AppData.hasReviewerRole(true)
		}));

		this.userpilotIdentified = true;
	}

	private shouldSkipUserpilotIdentify (url: string): boolean {
		const path = url.split('?')[0];

		// skip when in an unauthenticated page/url
		return path === '/dashboard' ||
			path === '/dashboard/' ||
			path.startsWith('/dashboard/auth/');
	}
}
